import {
    IStoredResource,
    IStoredResourceData,
    IVanillaReaderStreamedResourceStorage
} from "../VanillaReaderDataStore/VanillaReaderStreamedResourceIndexDbStorage";

/*
* # VanillaReaderShelfItemStoredResource
*
* ## RESPONSIBILITIES
*
* This class manages resource data for stored publications. It is created, retrieved and removed by calling various
* methods on the `VanillaReaderShelfItem` class.
*
* It is only used where a publication has stored its actual binary file contents on the user's device, it is not used for
* bookmarks, highlights etc.
*
* In the `VanillaReader` module this class is used by the `HttpCachingRandomAccessDataSource` to allow for the caching
* of streamed data in IndexedDB.
*
* "Under the hood" it uses and instance of `IVanillaReaderStreamedResourceStorage` to do execute the actual DB work.
*
* ## NOTE
*
* This implementation does not obfuscate the data's relationship to the shelf item. If you want to make it harder for
* hackers to extract publication data from the store, please use some kind of hashing method for the data before saving
* it to the database.
*
*
* ## RELATED TYPES
*
* - VanillaReaderShelfItem
* - VanillaReaderPublicationDataStore
* - VanillaReaderStreamedResourceStorage
*
* */
export class VanillaReaderShelfItemStoredResource implements IStoredResource {
    private _size: number | undefined;

    private constructor(private _streamedResourcesStore: IVanillaReaderStreamedResourceStorage, private _id: string, private _name: string, private _url: string, private _mimetype: string, _size?: number) {}

    static async instantiate(stateData: IStoredResource, streamedResourcesStore: IVanillaReaderStreamedResourceStorage): Promise<VanillaReaderShelfItemStoredResource> {
        return new VanillaReaderShelfItemStoredResource(streamedResourcesStore, stateData.id, stateData.name, stateData.url, stateData.mimetype, stateData.size);
    }

    static async create(streamedResourcesStore: IVanillaReaderStreamedResourceStorage, id: string, name: string, url: string, mimetype: string, size: number = 0): Promise<VanillaReaderShelfItemStoredResource> {
        if(!await streamedResourcesStore.hasResource(id)) {
            streamedResourcesStore.addResource({
                id,
                mimetype,
                url,
                name,
                size
            }).catch(console.error);

            return new VanillaReaderShelfItemStoredResource(streamedResourcesStore, id, name, url, mimetype, size);
        } else {
            throw new DOMException(`VanillaReaderShelfItemStoredResource.create(), Resource with id "${id}" already exists.`);
        }
    }

    get size(): number | undefined {
        return this._size;
    }

    set size(value: number | undefined) {
        this._size = value;
    }

    get mimetype(): string {
        return this._mimetype;
    }

    set mimetype(value: string) {
        this._mimetype = value;
    }

    get url(): string {
        return this._url;
    }

    set url(value: string) {
        this._url = value;
    }

    get name(): string {
        return this._name;
    }

    set name(value: string) {
        this._name = value;
    }

    get id(): string {
        return this._id;
    }

    set id(value: string) {
        this._id = value;
    }

    async hasData(): Promise<boolean> {
        return this._streamedResourcesStore.hasResource(this.id);
    }

    async hasChunkAtOffset(startOffset: number): Promise<boolean> {
        return await this._streamedResourcesStore.hasResourceDataByStartOffset(this.id, startOffset) || false;
    }

    async fetchChunkAtOffset(startOffset: number): Promise<IStoredResourceData | undefined> {
        return await this._streamedResourcesStore.getResourceDataByStartOffset(this.id, startOffset) || undefined;
    }

    async addChunk(startOffset: number, endOffset: number, arrayBuffer: ArrayBuffer): Promise<void> {
        return this._streamedResourcesStore.addResourceData(this.id, startOffset, endOffset, arrayBuffer);
    }

    async deleteData(): Promise<void> {
        return this._streamedResourcesStore.deleteResource(this.id);
    }
}
